import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend
} from 'recharts';
import { CreditCard, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// ─── Plan definitions ─────────────────────────────────────────────────────────
const PLANS = {
  kholop: {
    name: 'Kholop', price: 19, agents: 3, minutes: 200, color: '#3b82f6',
    features: ['3 AI agents', '200 min/month', 'Call history & transcripts', 'Email / SMS actions', 'Website embed'],
  },
  boyar: {
    name: 'Boyar', price: 49, agents: 10, minutes: 600, color: '#8b5cf6',
    features: ['10 AI agents', '600 min/month', 'Everything in Kholop', 'Advanced analytics', 'Webhook actions', 'Priority support'],
  },
  knyaz: {
    name: 'Knyaz', price: 99, agents: 'Unlimited', minutes: 2000, color: '#f59e0b',
    features: ['Unlimited agents', '2000 min/month', 'Everything in Boyar', 'Peak hour analytics', 'Full API access', 'Dedicated support'],
  },
};

const TOP_UP_AMOUNTS = [5, 10, 25, 50, 100];

// ─── Stripe inner form (must be inside <Elements>) ────────────────────────────
function PaymentForm({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setErrorMsg('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        setErrorMsg(error.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        const res = await api.post('/billing/topup/confirm', { payment_intent_id: paymentIntent.id });
        toast.success(res.data.message || `$${amount} added to your balance!`);
        onSuccess(res.data.new_balance);
      } else if (paymentIntent.status === 'processing') {
        toast('Payment is processing — balance will update shortly.', { icon: '⏳' });
        onSuccess(null);
      } else {
        setErrorMsg(`Unexpected status: ${paymentIntent.status}. Please contact support.`);
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-form">
      <div className="stripe-amount-header">
        <CreditCard size={20} />
        <span>Adding <strong>${amount} AUD</strong> to balance</span>
      </div>

      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMsg && (
        <div className="payment-error">
          <XCircle size={15} /> {errorMsg}
        </div>
      )}

      <div className="stripe-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!stripe || loading}>
          {loading ? 'Processing...' : `Pay $${amount} AUD`}
        </button>
      </div>
      <p className="stripe-security-note">🔒 Secured by Stripe — card details never touch our servers.</p>
    </form>
  );
}

// ─── Main Billing Page ────────────────────────────────────────────────────────
export default function BillingPage() {
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState({ usage: [], daily: [] });
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  const [stripePromise, setStripePromise] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [paymentStep, setPaymentStep] = useState(null); // { clientSecret, amount }

  const loadData = useCallback(async () => {
    try {
      const [ovRes, invRes, usageRes, stripeRes] = await Promise.all([
        api.get('/billing/overview'),
        api.get('/billing/invoices'),
        api.get('/billing/usage?days=30'),
        api.get('/billing/stripe-config'),
      ]);
      setOverview(ovRes.data);
      setInvoices(invRes.data.invoices || []);
      setUsage(usageRes.data);
      setStripeConfig(stripeRes.data);

      if (stripeRes.data.publishable_key) {
        setStripePromise(prev => prev || loadStripe(stripeRes.data.publishable_key));
      }
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTopUpClick = async (amount) => {
    if (!stripeConfig?.enabled) {
      toast.error('Stripe is not configured — add STRIPE_SECRET_KEY and STRIPE_PUBLISHABLE_KEY to your .env');
      return;
    }
    try {
      const res = await api.post('/billing/topup', { amount });
      setPaymentStep({ clientSecret: res.data.client_secret, amount });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not initialise payment');
    }
  };

  const handlePaymentSuccess = (newBalance) => {
    setPaymentStep(null);
    if (newBalance != null) setOverview(prev => prev ? { ...prev, balance: newBalance } : prev);
    setTimeout(loadData, 1000); // slight delay so webhook can land first
  };

  const handleChangePlan = async (planKey) => {
    const p = PLANS[planKey];
    if (!window.confirm(
      `Switch to ${p.name} ($${p.price}/month)?\n\n` +
      `$${p.price} will be deducted from your balance now.\n` +
      `Current balance: $${overview?.balance?.toFixed(2)}`
    )) return;
    try {
      const res = await api.post('/billing/change-plan', { plan: planKey });
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Plan change failed');
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  const plan = PLANS[overview?.plan];
  const minutesPct = plan ? Math.min(100, ((overview?.minutes_used || 0) / plan.minutes) * 100) : 0;
  const balanceLow = (overview?.balance || 0) < 10;

  return (
    <div className="page">
      <h1 className="page-title">Billing</h1>

      {balanceLow && !overview?.is_demo && (
        <div className="banner banner--warning">
          <AlertCircle size={16} />
          Low balance (${overview?.balance?.toFixed(2)}) — top up to keep your agents active.
        </div>
      )}

      {/* Hero */}
      <div className="billing-hero">
        <div className="billing-hero__item">
          <span className="billing-hero__label">Balance</span>
          <span className="billing-hero__value" style={{ color: balanceLow ? '#ef4444' : '#22c55e' }}>
            ${overview?.balance?.toFixed(2)}
          </span>
        </div>
        <div className="billing-hero__item">
          <span className="billing-hero__label">Plan</span>
          <span className="plan-badge" style={{ background: plan?.color || '#6b7280' }}>
            {(overview?.plan || 'demo').toUpperCase()}
          </span>
        </div>
        <div className="billing-hero__item" style={{ flex: 2 }}>
          <span className="billing-hero__label">
            Minutes: {overview?.minutes_used?.toFixed(0)} / {plan?.minutes || 50}
          </span>
          <div className="progress-bar">
            <div className="progress-bar__fill"
              style={{ width: `${minutesPct}%`, background: minutesPct > 90 ? '#ef4444' : '#3b82f6' }} />
          </div>
        </div>
      </div>

      {/* Stripe Payment Modal */}
      {paymentStep && stripePromise && (
        <div className="modal-overlay" onClick={() => setPaymentStep(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 20 }}>Top Up Balance</h2>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: paymentStep.clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#3b82f6',
                    colorBackground: '#1e293b',
                    colorText: '#f1f5f9',
                    colorDanger: '#ef4444',
                    fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
                    borderRadius: '6px',
                    spacingUnit: '4px',
                  },
                },
              }}
            >
              <PaymentForm
                amount={paymentStep.amount}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setPaymentStep(null)}
              />
            </Elements>
          </div>
        </div>
      )}

      <div className="tabs">
        {['Overview', 'Invoices', 'Usage & Stats'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'tab--active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === 'Overview' && (
        <div className="tab-content">
          <div className="billing-section">
            <h3>Top Up Balance</h3>
            <p className="helper-text">One-time top-ups. Funds never expire and cover call minutes + plan costs.</p>
            {!stripeConfig?.enabled && (
              <div className="banner banner--warning" style={{ marginBottom: 14 }}>
                <AlertCircle size={15} /> Stripe keys not configured — add them to your <code>.env</code> file.
              </div>
            )}
            <div className="topup-grid">
              {TOP_UP_AMOUNTS.map(amt => (
                <button key={amt} className="topup-btn" onClick={() => handleTopUpClick(amt)} disabled={!stripeConfig?.enabled}>
                  <span className="topup-amount">${amt}</span>
                  <span className="topup-label">AUD</span>
                </button>
              ))}
            </div>
          </div>

          <div className="billing-section">
            <h3>Plans</h3>
            <p className="helper-text">Plan cost is deducted from your balance monthly. Ensure your balance covers the plan cost.</p>
            <div className="plans-grid">
              {Object.entries(PLANS).map(([key, p]) => {
                const isCurrent = overview?.plan === key;
                const canAfford = (overview?.balance || 0) >= p.price;
                return (
                  <div key={key} className={`plan-card ${isCurrent ? 'plan-card--current' : ''}`}
                    style={{ '--plan-color': p.color, borderColor: isCurrent ? p.color : 'var(--border)' }}>
                    <div className="plan-header" style={{ background: p.color }}>
                      <h4>{p.name}</h4>
                      <span className="plan-price-tag">${p.price}<small>/mo</small></span>
                    </div>
                    <ul className="plan-features">
                      {p.features.map((f, i) => (
                        <li key={i}><CheckCircle size={12} style={{ color: p.color, flexShrink: 0 }} /> {f}</li>
                      ))}
                    </ul>
                    <div className="plan-action">
                      {isCurrent
                        ? <button className="btn btn-ghost btn-full" disabled>✓ Current Plan</button>
                        : <button className="btn btn-primary btn-full" onClick={() => handleChangePlan(key)}
                            disabled={!canAfford} title={!canAfford ? `Requires $${p.price} balance` : ''}>
                            {canAfford ? `Switch — $${p.price}/mo` : `Need $${p.price} balance`}
                          </button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Invoices ── */}
      {tab === 'Invoices' && (
        <div className="tab-content">
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th><th>Stripe Ref</th></tr>
            </thead>
            <tbody>
              {invoices.length === 0
                ? <tr><td colSpan={5} className="empty-row">No invoices yet</td></tr>
                : invoices.map(inv => (
                  <tr key={inv.id}>
                    <td>{new Date(inv.created_at * 1000).toLocaleDateString('en-AU')}</td>
                    <td>{inv.description || inv.type}</td>
                    <td><strong>${inv.amount?.toFixed(2)}</strong></td>
                    <td><span className={`status-badge status-badge--${inv.status}`}>{inv.status}</span></td>
                    <td>
                      {inv.stripe_payment_id
                        ? <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.stripe_payment_id.slice(0, 18)}…</code>
                        : '—'}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* ── Usage ── */}
      {tab === 'Usage & Stats' && (
        <div className="tab-content">
          {usage.daily?.length > 0 && (
            <div className="billing-section">
              <h3>Daily Activity (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={usage.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Calls" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="minutes" stroke="#8b5cf6" name="Minutes" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="billing-section">
            <h3>Usage by Agent</h3>
            {usage.usage?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={usage.usage}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                    <Legend />
                    <Bar dataKey="call_count" fill="#3b82f6" name="Calls" radius={[4,4,0,0]} />
                    <Bar dataKey="minutes_used" fill="#8b5cf6" name="Minutes" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
                <table className="data-table" style={{ marginTop: 16 }}>
                  <thead><tr><th>Agent</th><th>Calls</th><th>Minutes</th><th>Est. Cost</th></tr></thead>
                  <tbody>
                    {usage.usage.map(row => (
                      <tr key={row.id}>
                        <td><strong>{row.name}</strong></td>
                        <td>{row.call_count}</td>
                        <td>{Number(row.minutes_used).toFixed(1)}</td>
                        <td>${Number(row.total_cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : <p className="helper-text">No usage data in the last 30 days.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
